"use client";

import { BUTTON_SMALL, CARD, INPUT } from "@/components/tool-kit/styles";
import type { LatexTableStrings as Strings } from "@/components/tools/latex-table-generator";
import { t } from "@/lib/i18n/interpolate";
import {
  type ColumnAlign,
  type LatexTableOptions,
  normalizeRows,
} from "@/lib/tool-kit/developer/latex-table";

/*
 * The LaTeX Table Generator's grid editor and typeset-style preview. Every word comes
 * in through `strings`, which the widget owns.
 */

const ALIGN_CLASS: Record<ColumnAlign, string> = {
  l: "text-left",
  c: "text-center",
  r: "text-right",
};

function cellClass(
  options: LatexTableOptions,
  column: number,
  border: string
): string {
  return `px-3 py-1 ${ALIGN_CLASS[options.align[column] ?? "l"]} ${border}`;
}

export function GridEditor({
  grid,
  onChange,
  strings,
}: {
  grid: string[][];
  onChange: (grid: string[][]) => void;
  strings: Strings;
}) {
  const width = grid[0]?.length ?? 0;
  const setCell = (row: number, column: number, value: string) =>
    onChange(
      grid.map((cells, r) =>
        r === row
          ? cells.map((cell, c) => (c === column ? value : cell))
          : cells
      )
    );
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-1">
          <tbody>
            {grid.map((cells, row) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: grid rows are positional
              <tr key={row}>
                {cells.map((cell, column) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: grid cells are positional
                  <td key={column}>
                    <input
                      aria-label={t(strings.cellLabel, {
                        row: row + 1,
                        column: column + 1,
                      })}
                      className={`${INPUT} w-32 ${row === 0 ? "font-medium" : ""}`}
                      onChange={(event) =>
                        setCell(row, column, event.target.value)
                      }
                      type="text"
                      value={cell}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            onChange([...grid, Array.from({ length: width }, () => "")])
          }
          type="button"
        >
          {strings.addRow}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={grid.length <= 1}
          onClick={() => onChange(grid.slice(0, -1))}
          type="button"
        >
          {strings.removeRow}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => onChange(grid.map((cells) => [...cells, ""]))}
          type="button"
        >
          {strings.addColumn}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={width <= 1}
          onClick={() => onChange(grid.map((cells) => cells.slice(0, -1)))}
          type="button"
        >
          {strings.removeColumn}
        </button>
      </div>
    </div>
  );
}

export function Preview({
  rows,
  options,
  strings,
}: {
  rows: string[][];
  options: LatexTableOptions;
  strings: Strings;
}) {
  const table = normalizeRows(rows);
  const header = options.headerRow ? table[0] : null;
  const body = options.headerRow ? table.slice(1) : table;
  const grid = options.rules === "grid";
  const ruled = options.rules !== "none";
  const cellBorder = grid ? "border border-foreground/60" : "";
  const caption =
    options.float && options.caption.trim() ? options.caption : "";
  const notes = options.notes.map((note) => note.trim()).filter(Boolean);
  const captionNode = caption ? (
    <p className="text-center text-sm">{caption}</p>
  ) : null;
  return (
    <div className={`${CARD} overflow-x-auto`}>
      <div className="mx-auto w-fit space-y-2">
        {options.captionPosition === "above" ? captionNode : null}
        <table
          className={`border-collapse font-serif text-sm ${ruled ? "border-foreground border-y-2" : ""}`}
        >
          {notes.length > 0 ? (
            // Like threeparttable: the notes wrap at the table's own width.
            <caption className="caption-bottom space-y-1 pt-2 text-left text-xs">
              {notes.map((note, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: notes are positional
                <span className="block" key={index}>
                  {options.notePrefix && index === 0 ? (
                    <em>{strings.notePrefixPreview} </em>
                  ) : null}
                  {note}
                </span>
              ))}
            </caption>
          ) : null}
          {header ? (
            <thead className={ruled ? "border-foreground/70 border-b" : ""}>
              <tr>
                {header.map((cell, column) => {
                  const className = `${cellClass(options, column, cellBorder)} ${options.boldHeader ? "font-bold" : "font-normal"}`;
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                    <th className={className} key={column}>
                      {cell}
                    </th>
                  );
                })}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {body.map((cells, row) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
              <tr key={row}>
                {cells.map((cell, column) => {
                  const className = cellClass(options, column, cellBorder);
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                    <td className={className} key={column}>
                      {cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {options.captionPosition === "below" ? captionNode : null}
      </div>
    </div>
  );
}
